import { Deal } from "@/types/deal";
import { env } from "@/lib/env";
import { normalizeFacilitiesToArray } from "@/lib/deals/kpisNormalizers";
import { reconstructRoomRevenueModel } from "@/lib/deals/reconstructors";
import { getProject } from "@/lib/datasource";

export const logDealState = (stage: string, deal: any) => {
  if (!deal) {
    console.warn(
      `[DealWorkspace] logDealState called with null deal at stage: ${stage}`,
    );
    return;
  }
};

export function buildAmenitiesFromFacilities(facilities: any) {
  if (!Array.isArray(facilities))
    return {
      restaurant: false,
      bar: false,
      spa: false,
      gym: false,
      pool: false,
      parking: false,
      meetingsEvents: false,
      roomService: false,
    };

  const facilitiesLower = facilities
    .map((f: any) => {
      if (typeof f === "string") return f.toLowerCase();
      if (f && typeof f === "object")
        return (f.name || f.type || "").toLowerCase();
      return "";
    })
    .filter((f) => f);

  return {
    restaurant: facilitiesLower.includes("restaurant"),
    bar: facilitiesLower.includes("bar"),
    spa: facilitiesLower.includes("spa"),
    gym: facilitiesLower.includes("gym"),
    pool: facilitiesLower.includes("pool"),
    parking: facilitiesLower.includes("parking"),
    meetingsEvents:
      facilitiesLower.includes("conference") ||
      facilitiesLower.includes("meetingevents") ||
      facilitiesLower.includes("meetings"),
    roomService:
      facilitiesLower.includes("roomservice") ||
      facilitiesLower.includes("room_service") ||
      facilitiesLower.includes("other"),
  };
}

export async function loadAndHydrateDeal(dealId: string): Promise<Deal | null> {
  if (env.DATA_SOURCE !== "supabase") {
    return null;
  }
  try {
    const row = await getProject(dealId);
    if (!row) {
      console.error("[loadAndHydrateDeal] Deal not found in Supabase:", dealId);
      return null;
    }

    const projectData = Array.isArray(row) ? row[0] : row;

    let hydratedDeal: Deal = {
      id: projectData.id || dealId,
      createdAt: projectData.created_at || new Date().toISOString(),
      updatedAt: projectData.updated_at || new Date().toISOString(),
      name: projectData.name || "Untitled Deal",
      location: projectData.kpis?.location || "",
      address: projectData.kpis?.address || "",
      propertyType: (projectData.kpis?.propertyType ||
        "Boutique Hotel") as Deal["propertyType"],
      stars: (projectData.kpis?.starRating || 4) as Deal["stars"],
      starRating: (projectData.kpis?.starRating || 4) as Deal["starRating"],
      currency: (projectData.kpis?.currency ||
        projectData.currency ||
        "EUR") as Deal["currency"],
      purchasePrice: projectData.kpis?.purchase?.price || 0,
      gfaSqm: projectData.kpis?.gfaSqm || 0,
      photoUrl: projectData.kpis?.photoUrl || "",
      facilities: normalizeFacilitiesToArray(
        projectData.kpis?.facilities || [],
      ),
      roomTypes: Array.isArray(projectData.kpis?.roomMix)
        ? projectData.kpis.roomMix.map((rm: any) => ({
            id: rm.id || crypto.randomUUID(),
            name: rm.type || rm.name || "Standard",
            rooms: rm.count || rm.rooms || 0,
            adrWeight: rm.adrWeight || 100,
          }))
        : [],
      rooms: Array.isArray(projectData.kpis?.roomMix)
        ? projectData.kpis.roomMix.map((rm: any) => ({
            id: rm.id || crypto.randomUUID(),
            type: rm.type || rm.name || "Standard",
            count: rm.count || rm.rooms || 0,
            sqm: rm.sqm || 25,
            adrBase: rm.adr || rm.baseAdr || 100,
          }))
        : [],
      normalizedRooms: Array.isArray(projectData.kpis?.roomMix)
        ? projectData.kpis.roomMix.map((rm: any) => ({
            id: rm.id || crypto.randomUUID(),
            name: rm.type || rm.name || "Standard",
            count: rm.count || rm.rooms || 0,
            sqm: rm.sqm,
            adrBase: rm.adr || rm.baseAdr,
          }))
        : [],
      totalRooms: projectData.kpis?.rooms?.total || 0,
      amenities: buildAmenitiesFromFacilities(
        projectData.kpis?.facilities || [],
      ),
      kpis: projectData.kpis || {},
    };

    if (projectData.kpis) {
      const kpis = projectData.kpis;
      const facilities = hydratedDeal.facilities || [];
      if (facilities.length > 0) {
        hydratedDeal.kpis.facilities = facilities;
        if (!hydratedDeal.kpis.property) hydratedDeal.kpis.property = {};
        hydratedDeal.kpis.property.facilities = facilities;
      }
      hydratedDeal.amenities = buildAmenitiesFromFacilities(facilities);

      if (!hydratedDeal.budget && kpis.budget) {
        const b = kpis.budget;
        hydratedDeal.budget = {
          netPurchasePrice: Number(kpis.purchase?.price ?? 0),
          reTransferTax: Number(b.transferTax ?? 0),
          dealCosts: Number(b.dealCosts ?? 0),
          siteAcquisition: 0,
          constructionCosts: Number(b.construction ?? 0),
          ffeOse: Number(b.ffoeOse ?? 0),
          constructionSubtotal: 0,
          professionalFees: Number(b.profFees ?? 0),
          planningCharges: Number(b.planningFees ?? 0),
          developmentFee: Number(b.devFee ?? 0),
          developmentSubtotal: 0,
          insuranceAdmin: Number(b.insuranceAdmin ?? 0),
          otherDev: Number(b.other ?? 0),
          otherDevSubtotal: 0,
          preOpening: Number(b.preopening ?? 0),
          preOpeningSubtotal: 0,
          contingencyPct: Number(b.contingencyPct ?? 10),
          contingencyAmount: 0,
          grandTotal: 0,
        };
        const siteAcquisition =
          hydratedDeal.budget.netPurchasePrice +
          hydratedDeal.budget.reTransferTax +
          hydratedDeal.budget.dealCosts;
        const constructionSubtotal =
          hydratedDeal.budget.constructionCosts + hydratedDeal.budget.ffeOse;
        const developmentSubtotal =
          hydratedDeal.budget.professionalFees +
          hydratedDeal.budget.planningCharges +
          hydratedDeal.budget.developmentFee;
        const otherDevSubtotal =
          hydratedDeal.budget.insuranceAdmin + hydratedDeal.budget.otherDev;
        const preOpeningSubtotal = hydratedDeal.budget.preOpening;

        const developmentBase =
          constructionSubtotal +
          developmentSubtotal +
          otherDevSubtotal +
          preOpeningSubtotal;

        const contingencyAmount =
          (hydratedDeal.budget.contingencyPct / 100) *
          (developmentBase + hydratedDeal.budget.netPurchasePrice);

        const grandTotal =
          developmentBase + siteAcquisition + contingencyAmount;

        hydratedDeal.budget.siteAcquisition = siteAcquisition;
        hydratedDeal.budget.constructionSubtotal = constructionSubtotal;
        hydratedDeal.budget.developmentSubtotal = developmentSubtotal;
        hydratedDeal.budget.otherDevSubtotal = otherDevSubtotal;
        hydratedDeal.budget.preOpeningSubtotal = preOpeningSubtotal;
        hydratedDeal.budget.contingencyAmount = contingencyAmount;
        hydratedDeal.budget.grandTotal = grandTotal;

        if (!kpis.investmentBudget) kpis.investmentBudget = {};
        kpis.investmentBudget.grandTotal = grandTotal;
      }

      if (kpis.gfaSqm != null) {
        const gfaFromKpis = Number(kpis.gfaSqm);
        if (Number.isFinite(gfaFromKpis) && gfaFromKpis > 0) {
          if (hydratedDeal.gfaSqm == null || Number(hydratedDeal.gfaSqm) === 0)
            hydratedDeal.gfaSqm = gfaFromKpis;
          if (
            (hydratedDeal as any).buildingGfaSqm == null ||
            Number((hydratedDeal as any).buildingGfaSqm) === 0
          ) {
            (hydratedDeal as any).buildingGfaSqm = gfaFromKpis;
          }
        }
      }

      if (!hydratedDeal.fnbRevenue && kpis.fnb) {
        const fnb = kpis.fnb;
        hydratedDeal.fnbRevenue = {
          mode: "advanced",
          simple: {
            avgGuestsPerOccRoom: fnb.avgGuests || 1.5,
            totalGuestCapturePct: 225,
            avgCheckGuest: 20,
            externalCoversPerDay: 65,
            avgCheckExternal: 25,
          },
          advanced: {
            breakfast: {
              key: "breakfast",
              label: "Breakfast",
              icon: "☕",
              guestCapturePct: Number(fnb.capture?.breakfast ?? 0),
              avgCheckGuest: Number(fnb.check?.guest?.breakfast ?? 0),
              externalCoversPerDay: Number(
                fnb.external?.perDay?.breakfast ?? 0,
              ),
              avgCheckExternal: Number(fnb.check?.external?.breakfast ?? 0),
            },
            lunch: {
              key: "lunch",
              label: "Lunch",
              icon: "🥗",
              guestCapturePct: Number(fnb.capture?.lunch ?? 0),
              avgCheckGuest: Number(fnb.check?.guest?.lunch ?? 0),
              externalCoversPerDay: Number(fnb.external?.perDay?.lunch ?? 0),
              avgCheckExternal: Number(fnb.check?.external?.lunch ?? 0),
            },
            dinner: {
              key: "dinner",
              label: "Dinner",
              icon: "🍽️",
              guestCapturePct: Number(fnb.capture?.dinner ?? 0),
              avgCheckGuest: Number(fnb.check?.guest?.dinner ?? 0),
              externalCoversPerDay: Number(fnb.external?.perDay?.dinner ?? 0),
              avgCheckExternal: Number(fnb.check?.external?.dinner ?? 0),
            },
            bar: {
              key: "bar",
              label: "Bar",
              icon: "🍷",
              guestCapturePct: Number(fnb.capture?.bar ?? 0),
              avgCheckGuest: Number(fnb.check?.guest?.bar ?? 0),
              externalCoversPerDay: Number(fnb.external?.perDay?.bar ?? 0),
              avgCheckExternal: Number(fnb.check?.external?.bar ?? 0),
            },
          },
          distributionWeights: {
            breakfast: 30,
            lunch: 25,
            dinner: 35,
            bar: 10,
          },
        };
      }

      if (!hydratedDeal.roomRevenue && (kpis.roomRevenue || kpis.roomRev)) {
        const roomRevData = kpis.roomRevenue || {};
        const roomRevTemplate = kpis.roomRev || {};
        const totalRooms =
          kpis.rooms?.total ||
          (Array.isArray(kpis.roomMix)
            ? kpis.roomMix.reduce(
                (sum: number, rm: any) => sum + (rm.count || rm.rooms || 0),
                0,
              )
            : 0);

        if (
          roomRevData.monthly &&
          Array.isArray(roomRevData.monthly) &&
          roomRevData.monthly.length > 0
        ) {
          (hydratedDeal as any).roomRevenue = {
            months: roomRevData.monthly.map((m: any, i: number) => ({
              month: i + 1,
              adr: m.adr || 0,
              occPct: m.occ || m.occPct || 0,
              occRooms: m.occRooms || 0,
              roomNights: m?.roomsAvailable || m.roomNights || 0,
              roomsRevenue: m.roomsRevenue || 0,
              revPAR: m.revPAR || 0,
            })),
            seasonalityPreset: roomRevData.preset || null,
            totals: roomRevData.totals || {},
          };
        } else if (
          roomRevTemplate.adr?.length > 0 &&
          roomRevTemplate.occ?.length > 0
        ) {
          (hydratedDeal as any).roomRevenue = reconstructRoomRevenueModel(
            roomRevTemplate.adr,
            roomRevTemplate.occ,
            totalRooms,
            kpis.currency || "EUR",
          );
        } else if (roomRevData.adr?.length > 0 && roomRevData.occ?.length > 0) {
          (hydratedDeal as any).roomRevenue = reconstructRoomRevenueModel(
            roomRevData.adr,
            roomRevData.occ,
            totalRooms,
            kpis.currency || "EUR",
          );
        }
      }
      if (hydratedDeal.currency == null && kpis.currency)
        hydratedDeal.currency = kpis.currency;
      if (hydratedDeal.starRating == null && kpis.starRating != null)
        hydratedDeal.starRating = kpis.starRating;
      if (hydratedDeal.location == null && kpis.location)
        hydratedDeal.location = kpis.location;
      if (hydratedDeal.purchasePrice == null && kpis.purchase?.price != null)
        hydratedDeal.purchasePrice = kpis.purchase.price;
    }

    logDealState("After hydration", hydratedDeal);
    return hydratedDeal;
  } catch (e) {
    console.error("[loadAndHydrateDeal] Failed to load from Supabase:", e);
    return null;
  }
}
